'use client';

import { Session } from 'next-auth';
import { usePathname } from 'next/navigation';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { FundingType, JourneyType, ProductType, ResourceType } from '@bytel/leviator';
import { CartContext } from '@bytel/leviator/react';
import { Accordion, Container, Section, Spacer, SpacerSize, TrilogyColor } from '@bytel/trilogy-react';

import {
    getDefaultFunding,
    getPhoneCurrentPlan,
    getPlanTypeFromUrlKey,
    productDetailsService,
} from '@services/products';
import { TradeInEligibilityContext, getPartnerProduct } from '@services/trade-in';

import { AnimcoType } from '@app-types/animco';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { EasyTradeInChoice } from '@app-types/easy-trade-in';
import { SearchParamsType } from '@app-types/page';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull, ProductParentFull } from '@app-types/product';
import { UserType } from '@app-types/user';

import { AnimcoBanner } from '@components/animco-banner';
import { ContractSelectionModal } from '@components/contract-selection-modal';
import { FundingLegals } from '@components/funding-legals';
import { Konami } from '@components/konami';
import { Legals } from '@components/legals';
import { EnergyLabelModal, ModalPlanDetails } from '@components/product-details-modal';
import { StructuredData } from '@components/structured-data';
import { TriggerLogin } from '@components/trigger-login';

import { PartialMarketingsState, useMarketings } from '@hooks/use-marketings';
import { useOnScreen } from '@hooks/use-on-screen';

import { replaceWindowUrl, setDocumentTitle } from '@helpers/document';
import { dvdMove } from '@helpers/konami';
import { LegalsSlugType } from '@helpers/partials';
import { paths } from '@helpers/path';
import { getPhoneStructuredData } from '@helpers/structured-data';

import { EASY_TRADE_IN_REFERENCE_STALE_TIME } from '@constants/easy-trade-in';
import { MarketingType, PlanSubTypeId, PlanTypeId } from '@constants/plan';
import { isProduct3dModalEnabled } from '@constants/product-3d';

import {
    AnimcoBox,
    BackLink,
    Customization,
    EasyTradeInModal,
    MobileTradeInModal,
    ModalStoreAvailability,
    Plans,
    ProductHeader,
    ProductOffersDiscounts,
    ProductPreview,
    ProductSpecifications,
    ProductStickyBox,
    Reinsurance,
    Tabs,
    TabsEnum,
    Ticket,
    Viewer3dModal,
} from '@product-detail/components';
import {
    pageTrackingHandler,
    sendProductPlanPriceRecapTracking,
    sendProductPortabilityTracking,
    sendProductStockTracking,
    sendProductTabsTracking,
} from '@product-detail/helpers';
import { useEasyTradeIn, usePlanConfiguration, useProductPrice, useTradeIn } from '@product-detail/hooks';
import { useQuery } from '@tanstack/react-query';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { Breadcrumb } from './breadcrumb';
import { SmartChangeModal } from './plans/smart-change-modal';

const typesWithCartHandling = new Set([WallType.PHONE, WallType.HOTSPOT]);

export type ProductDetailProps = {
    type: WallType;
    isRenewal?: boolean;
    baseUrl: string;
    product: ProductParentFull;
    plans?: MobilePlanType[];
    currentProduct: ProductChildFull;
    enableKonami?: boolean;
    animco: AnimcoType;
    tradeInBonusPlan?: string;
    searchParams?: SearchParamsType;
    contract?: ContractType;
    session?: Session;
    slug: LegalsSlugType;
    planTypeId?: PlanTypeId;
    idPu?: string;
    easyTradeInEnabled: boolean;
};

export function ProductDetail({
    type,
    isRenewal = false,
    baseUrl,
    product: _product,
    plans: _plans = [],
    currentProduct: _currentProduct,
    animco,
    tradeInBonusPlan,
    searchParams,
    contract,
    session,
    enableKonami = false,
    slug,
    planTypeId: _planTypeId = PlanTypeId.PREMIUM,
    idPu,
    easyTradeInEnabled,
}: Readonly<ProductDetailProps>) {
    // --- Context & Routing ---
    const user = session?.user as UserType | undefined;
    const { cart, isLoading } = useContext(CartContext);
    const pathname = usePathname();

    // --- States ---
    const [product, setProduct] = useState<ProductParentFull>(_product);
    const [selectedProduct, setSelectedProduct] = useState<ProductChildFull>(_currentProduct);
    const [activeTab, setActiveTab] = useState<TabsEnum>(TabsEnum.CHARACTERISTICS);
    const [hasPlanModalOpen, setHasPlanModalOpen] = useState<boolean>(false);
    const [hasModalStoreAvailabilityOpen, setHasModalStoreAvailabilityOpen] = useState(false);
    const [isEnergyLabelModalOpen, setIsEnergyLabelModalOpen] = useState(false);
    const [viewer3dInitialColor, setViewer3dInitialColor] = useState<string>();
    const [hasSmartChangeModalOpen, setHasSmartChangeModalOpen] = useState<boolean>(false);

    const planInCart = cart?.getJourneyOfType(JourneyType.MOBILE_ACQUISITION)?.getPrincipalProduct();

    // --- Memos ---
    const journey = useMemo(() => {
        if (!typesWithCartHandling.has(type)) {
            return undefined;
        }

        if (isRenewal && contract?.id) {
            return cart?.getJourneyOfType(JourneyType.RENEWAL, contract.id);
        }

        return cart?.getJourneyOfType(JourneyType.MOBILE_ACQUISITION);
    }, [cart, isRenewal, type, contract?.id]);

    const planTypeId = useMemo(() => {
        if (isRenewal || type !== WallType.PHONE) {
            return _planTypeId;
        }
        const planFromCart = journey?.getProductsByType(ProductType.MAIN_MOBILE_OFFER)?.[0];
        const urlParams = new URLSearchParams(searchParams);

        if (planFromCart?.metadata?.urlKey) {
            return getPlanTypeFromUrlKey(planFromCart.metadata.urlKey);
        } else if (urlParams.get('forfait')) {
            return getPlanTypeFromUrlKey(urlParams.get('forfait'));
        }

        return _planTypeId;
    }, [isRenewal, type, _planTypeId, journey, searchParams]);

    // --- Queries ---
    const plansQuery = useQuery({
        queryKey: ['product-details-plans', selectedProduct.gencode, isRenewal, planTypeId, contract?.id, session],
        queryFn: () =>
            productDetailsService.getPlans(selectedProduct.gencode, isRenewal, planTypeId, contract, session),
        enabled: typesWithCartHandling.has(type),
        initialData: _plans,
    });

    const defaultPlan = useMemo(() => {
        if (!typesWithCartHandling.has(type)) {
            return undefined;
        }

        const urlSearchParams = new URLSearchParams(searchParams);
        const planInCartGencode = journey?.getProductsByType(ProductType.MAIN_MOBILE_OFFER)?.[0]?.gencode;

        return getPhoneCurrentPlan(
            type,
            plansQuery.data,
            planInCartGencode,
            isRenewal,
            urlSearchParams.get('sousType') === 'smart-change',
            urlSearchParams.get('forfait'),
            contract,
        );
    }, [type, plansQuery.data, journey, isRenewal, searchParams, contract, planTypeId]);

    const defaultFunding = useMemo(() => {
        const urlSearchParams = new URLSearchParams(searchParams);

        return defaultPlan
            ? getDefaultFunding(defaultPlan.fundings, urlSearchParams.get('financement')?.toUpperCase() ?? undefined)
            : undefined;
    }, [defaultPlan, searchParams]);

    // --- Custom Hooks ---
    const [marketings, setMarketings] = useMarketings(defaultPlan?.details?.discounts ?? [], user);
    const [hasSimulatedPortabilitySelected, setHasSimulatedPortabilitySelected] = useState(() =>
        (defaultPlan?.details?.discounts ?? []).some((discount) =>
            discount.marketingTypes.includes(MarketingType.PORTABILITY),
        ),
    );
    const {
        plan: { selectedPlan, setSelectedPlan, planSubType },
        funding: { selectedFunding, setSelectedFunding },
    } = usePlanConfiguration(
        plansQuery.data,
        defaultPlan,
        defaultFunding?.key,
        (market: PartialMarketingsState) => setMarketings({ ...marketings, ...market }),
        user,
    );

    const hasPortabilityDiscount = useMemo(
        () =>
            (selectedPlan?.details?.discounts ?? []).some((discount) =>
                discount.marketingTypes.includes(MarketingType.PORTABILITY),
            ),
        [selectedPlan],
    );

    useEffect(() => {
        if (planInCart) {
            const hasSimulatedPortability = planInCart.allocatedResources.some(
                (resource) => resource.type === ResourceType.PORTABILITY_SIMULATED,
            );
            setHasSimulatedPortabilitySelected(hasSimulatedPortability);
        } else {
            setHasSimulatedPortabilitySelected(hasPortabilityDiscount);
        }
    }, [planInCart, hasPortabilityDiscount]);

    const onUpdatePortability = (isSelected: boolean) => {
        setHasSimulatedPortabilitySelected(isSelected);
        sendProductPortabilityTracking(isRenewal, isSelected ? 'Oui' : 'Non');
    };

    const { prices, noPlanPrice } = useProductPrice(
        selectedProduct,
        selectedPlan,
        selectedFunding,
        marketings,
        hasSimulatedPortabilitySelected,
    );

    const {
        tradeInStep,
        setTradeInStep,
        hasTradeIn,
        tradeInBonus,
        hasTradeInModalOpen,
        setHasTradeInModalOpen,
        handleOpenTradeInModal,
        resetTradeIn,
    } = useTradeIn({
        selectedProductGencode: selectedProduct.gencode,
        parentProductGencode: product.gencode,
        selectedPlan,
    });

    const userContext: TradeInEligibilityContext = {
        isRenewal,
        idPu,
        contractId: contract?.id,
        user,
    };

    const easyTradeIn = useEasyTradeIn({
        ...userContext,
        enabled: type === WallType.PHONE,
    });

    const easyTradeInCta = {
        isEnabled: easyTradeInEnabled,
        isEligible: easyTradeIn.eligibility.hasEasyTradeIn,
        choice: easyTradeIn.choice,
        isConfirmed: easyTradeIn.isConfirmed,
        partnerId: easyTradeIn.confirmedResult?.partnerRequestId,
        creditId: easyTradeIn.eligibility.creditId,
        triggerReturnHint: easyTradeIn.triggerReturnHint,
    };

    const { hasEasyTradeIn, hasClassicTradeIn } = easyTradeIn.eligibility;
    const shouldShowClassicTradeInBlock = hasEasyTradeIn
        ? easyTradeIn.choice === EasyTradeInChoice.KEEP && hasClassicTradeIn
        : hasClassicTradeIn;

    const { isVisible: isTicketVisible, ref: ticketRef } = useOnScreen();
    const { isVisible: isStickyVisible, ref: stickyRef } = useOnScreen();

    // --- Tracking ---
    const sendPageTracking = useCallback(
        (selectedProductToTrack: ProductChildFull) =>
            pageTrackingHandler(type, isRenewal, _product, animco, contract)(selectedProductToTrack),
        [type, isRenewal, _product, animco, contract],
    );
    // --- Event Handlers ---
    const updatePageUrl = (newProduct: ProductChildFull) => {
        setDocumentTitle(newProduct.title);
        replaceWindowUrl(paths.productDetail(type, newProduct.urlKey, isRenewal));
    };

    const handleProductStock = async (newProduct: ProductChildFull) => {
        const stock = await productDetailsService.getProductStock(newProduct.gencode, {
            user,
            contractId: isRenewal ? contract?.id : undefined,
        });
        const newProductWithStock = { ...newProduct, stockDetails: stock };

        setProduct((prev) => {
            const updatedChildren = Object.fromEntries(
                Object.entries(prev.childs).map(([key, child]) =>
                    key === newProductWithStock.gencode ? [key, newProductWithStock] : [key, child],
                ),
            );
            return { ...prev, childs: updatedChildren };
        });

        setSelectedProduct(newProductWithStock);
        sendPageTracking(newProductWithStock);
    };

    const handleSelectedProduct = (newProduct: ProductChildFull) => {
        setSelectedProduct(newProduct);

        if (newProduct.stockDetails) {
            sendPageTracking(newProduct);
        } else {
            void handleProductStock(newProduct);
        }

        updatePageUrl(newProduct);
    };

    const onChangeProduct = async (color: string, capacity?: string) => {
        let selectedChild: ProductChildFull | undefined;

        if (capacity) {
            if (product.featuresRules[color]?.includes(capacity)) {
                selectedChild = Object.values(product.childs).find(
                    (child) => child.color === color && child.capacity === capacity,
                );
            } else {
                selectedChild = Object.values(product.childs).find((child) => {
                    const defaultCapacity = product.featuresRules[color]?.[0];
                    return child.color === color && child.capacity === defaultCapacity;
                });
            }
        } else {
            selectedChild = Object.values(product.childs).find((child) => child.color === color);
        }

        if (!selectedChild) return;
        handleSelectedProduct(selectedChild);
    };

    const handleViewer3dColorChange = (color: string) => {
        const feature = product.features.color.find(({ name }) => name.toLowerCase() === color.toLowerCase());

        if (feature && feature.name !== selectedProduct.color) {
            void onChangeProduct(feature.name, selectedProduct.capacity);
        }
    };

    const handleOpenAvailability: ClickEvent = (event) => {
        setHasModalStoreAvailabilityOpen(true);
        sendProductStockTracking(isRenewal, (event as Event & { target: HTMLInputElement }).target.innerText);
    };

    const handleOpenPlanModal = () => {
        setHasPlanModalOpen(true);
        sendProductPlanPriceRecapTracking(isRenewal, 'Voir plus de détails');
    };

    const handleOpenSmartChangeModal = () => {
        setHasSmartChangeModalOpen(true);
    };

    const handleSetActiveTab = (tab: TabsEnum) => {
        setActiveTab(tab);
        sendProductTabsTracking(isRenewal, tab);
    };

    const partnerProductId = easyTradeIn.eligibility.partnerProductId;
    const context = easyTradeIn.context;

    const { data: partnerProduct, isLoading: isLoadingProduct } = useQuery({
        queryKey: ['easy-trade-in-partner-product', partnerProductId, context.user?.login],
        queryFn: () => getPartnerProduct(partnerProductId!, context.user ?? null),
        enabled: !!partnerProductId,
        staleTime: EASY_TRADE_IN_REFERENCE_STALE_TIME,
    });

    // --- Effects ---
    useEffect(() => {
        window.history.scrollRestoration = 'manual';
    }, []);

    useEffect(() => {
        sendPageTracking(_currentProduct);
    }, [_currentProduct, sendPageTracking]);

    return (
        <Section backgroundColor={TrilogyColor.NEUTRAL_FADE} className='is-paddingless'>
            <TriggerLogin />
            <Section backgroundColor={TrilogyColor.BACKGROUND} className='is-paddingless'>
                <BackLink baseUrl={baseUrl} type={type} />
            </Section>
            <AnimcoBanner banner={animco.banner[0]} />
            <Spacer size={SpacerSize.SIX} className='is-hidden-mobile' />
            <ProductStickyBox
                type={type}
                currentProduct={selectedProduct}
                currentPlan={selectedPlan}
                openShopAvailabilityModal={handleOpenAvailability}
                isTicketShowed={isTicketVisible && !isStickyVisible}
                isRenewal={isRenewal}
                prices={prices}
                contract={contract}
                isLoading={isLoading}
                showRef={stickyRef}
                idPu={idPu}
                simulatedPortabilitySelected={hasSimulatedPortabilitySelected}
                easyTradeIn={easyTradeInCta}
            />
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE} data-type={type}>
                <Container medium className='product-page-layout'>
                    <ProductHeader
                        type={type}
                        currentProduct={selectedProduct}
                        reviews={product.reviews}
                        setActiveTab={handleSetActiveTab}
                        isSmartChange={plansQuery.data.some((plan) => plan.subTypeId === PlanSubTypeId.SMART_CHANGE)}
                        handleOpenSmartChangeModal={handleOpenSmartChangeModal}
                    />
                    <ProductPreview
                        type={type}
                        features={product.features}
                        currentProduct={selectedProduct}
                        setCurrentProduct={onChangeProduct}
                        isRenewal={isRenewal}
                        openEnergyLabelModal={() => setIsEnergyLabelModalOpen(true)}
                        viewer3d={product.viewer3d}
                        openViewer3dModal={
                            isProduct3dModalEnabled ? () => setViewer3dInitialColor(selectedProduct.color) : undefined
                        }
                        isViewer3dModalOpen={isProduct3dModalEnabled && viewer3dInitialColor !== undefined}
                    />
                    <div className='product-characteristics'>
                        <Spacer size={SpacerSize.FIVE} />
                        <ProductSpecifications
                            type={type}
                            product={selectedProduct}
                            setActiveTab={handleSetActiveTab}
                            specificationsCms={product.specificationsCms}
                        />
                        <AnimcoBox animco={animco.box} />
                        <Customization
                            type={type}
                            features={product.features}
                            featuresRules={product.featuresRules}
                            currentProduct={selectedProduct}
                            handleProductChange={onChangeProduct}
                            openShopAvailabilityModal={handleOpenAvailability}
                            isRenewal={isRenewal}
                        />

                        {(type === WallType.PHONE || type === WallType.HOTSPOT) && selectedPlan !== undefined && (
                            <Plans
                                type={type}
                                plans={plansQuery.data}
                                currentPlan={selectedPlan}
                                setCurrentPlan={setSelectedPlan}
                                setMarketings={setMarketings}
                                marketings={marketings}
                                openPlanModal={handleOpenPlanModal}
                                setFunding={setSelectedFunding}
                                fundingKey={selectedFunding?.key}
                                planSubType={planSubType}
                                isLoading={isLoading}
                                user={user}
                                isRenewal={isRenewal}
                                contract={contract}
                                prices={prices}
                                withoutPlanPrice={noPlanPrice}
                                handleOpenSmartChangeModal={handleOpenSmartChangeModal}
                                simulatedPortabilitySelected={hasSimulatedPortabilitySelected}
                                onUpdatePortability={onUpdatePortability}
                                hasEasyTradeIn={hasEasyTradeIn}
                                easyTradeIn={easyTradeIn}
                                tradeInBonus={tradeInBonus}
                                easyTradeInEnabled={easyTradeInEnabled}
                                hasTradeIn={hasTradeIn}
                                resetTradeIn={resetTradeIn}
                            />
                        )}

                        {type === WallType.PHONE && (shouldShowClassicTradeInBlock || !easyTradeInEnabled) && (
                            <ProductOffersDiscounts
                                product={product}
                                tradeInBonusPlan={tradeInBonusPlan}
                                openTradeInModal={handleOpenTradeInModal}
                                tradeInStep={tradeInStep}
                                resetTradeIn={resetTradeIn}
                                tradeInBonus={tradeInBonus}
                                isRenewal={isRenewal}
                                plan={selectedPlan}
                            />
                        )}
                        <Ticket
                            type={type}
                            currentProduct={selectedProduct}
                            plan={selectedPlan}
                            openPlanModal={handleOpenPlanModal}
                            openShopAvailabilityModal={handleOpenAvailability}
                            tradeInStep={tradeInStep}
                            tradeInBonus={tradeInBonus}
                            openTradeInModal={handleOpenTradeInModal}
                            selectedFunding={selectedFunding}
                            isLoading={isLoading}
                            showRef={ticketRef}
                            contract={contract}
                            prices={prices}
                            isRenewal={isRenewal}
                            idPu={idPu}
                            simulatedPortabilitySelected={hasSimulatedPortabilitySelected}
                            easyTradeIn={easyTradeInCta}
                            easyConfirmedResult={easyTradeIn.isConfirmed ? easyTradeIn.confirmedResult : null}
                            returnedPhoneName={`${partnerProduct?.phoneInfo.brand} ${partnerProduct?.phoneInfo.name}`}
                        />
                    </div>
                </Container>
                <Container medium>
                    <Spacer size={SpacerSize.SIX} />
                    <Reinsurance isRenewal={isRenewal} />
                </Container>
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Tabs
                    type={type}
                    product={product}
                    currentProduct={selectedProduct}
                    activeTab={activeTab}
                    setActiveTab={handleSetActiveTab}
                />
            </Section>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Breadcrumb type={type} product={product} isRenewal={isRenewal} />
                    <Accordion>
                        {selectedFunding &&
                            (selectedFunding.type === FundingType.SWAPPY ||
                                selectedFunding.type === FundingType.YOUNITED) && (
                                <FundingLegals funding={selectedFunding} />
                            )}
                        <Legals id='product-detail-legals' url={slug} isItem />
                    </Accordion>
                </Container>
            </Section>
            {hasModalStoreAvailabilityOpen && (
                <ModalStoreAvailability
                    onClose={() => setHasModalStoreAvailabilityOpen(false)}
                    currentProduct={selectedProduct}
                    campaignId={product.campaignId ?? ''}
                />
            )}
            {isEnergyLabelModalOpen && selectedProduct.characteristics.energyLabel && (
                <EnergyLabelModal
                    onClose={() => setIsEnergyLabelModalOpen(false)}
                    energyLabel={selectedProduct.characteristics.energyLabel}
                />
            )}
            {isProduct3dModalEnabled && viewer3dInitialColor !== undefined && product.viewer3d && (
                <Viewer3dModal
                    viewer={product.viewer3d}
                    initialColor={viewer3dInitialColor}
                    isRenewal={isRenewal}
                    onColorChange={handleViewer3dColorChange}
                    onClose={() => setViewer3dInitialColor(undefined)}
                />
            )}
            <MobileTradeInModal
                isOpen={hasTradeInModalOpen}
                onClose={() => setHasTradeInModalOpen(false)}
                step={tradeInStep}
                setStep={setTradeInStep}
                productName={product.name}
                tradeInBonusPlan={tradeInBonusPlan}
                tradeInBonus={tradeInBonus}
                isRenewal={isRenewal}
                session={session ?? null}
                userContext={userContext}
            />
            <EasyTradeInModal
                isOpen={easyTradeIn.isModalOpen}
                onClose={easyTradeIn.closeModal}
                partnerProduct={partnerProduct}
                isLoadingProduct={isLoadingProduct ?? false}
                partnerProductId={easyTradeIn.eligibility.partnerProductId}
                creditId={easyTradeInCta.creditId}
                context={easyTradeIn.context}
                onConfirm={easyTradeIn.confirm}
                onKeepPhone={easyTradeIn.keepPhone}
                tradeInBonus={tradeInBonus}
            />
            {selectedPlan && (
                <ModalPlanDetails
                    plan={selectedPlan}
                    price={prices.planPrices!}
                    isOpen={hasPlanModalOpen}
                    onClose={() => setHasPlanModalOpen(false)}
                    isRenewal={isRenewal}
                />
            )}
            <SmartChangeModal isOpen={hasSmartChangeModalOpen} handleClose={() => setHasSmartChangeModalOpen(false)} />
            {isRenewal && !contract && <ContractSelectionModal />}
            {enableKonami && (
                <Konami
                    action={() => {
                        dvdMove('productPreview', isRenewal);
                    }}
                />
            )}
            <StructuredData
                data={getPhoneStructuredData(
                    type,
                    pathname,
                    product,
                    selectedProduct,
                    prices.productPrices.marketingAmount,
                )}
                data-cy='structured-data'
            />
        </Section>
    );
}
